import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { ArrowUpDown, ChevronDown, Bot } from "lucide-react";

import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Button,
  Label,
  Badge,
} from "@/components/ui";
import { FormBudgets } from "@/components/common";

import { BudgetResponse } from "@/types";
import { formatDate, formatValue } from "@/utils";
import { useCopilotStore } from "@/stores/use-copilot-store";

import { Pagination } from "../../pagination";

type VoucherDataTableProps = {
  data: BudgetResponse;
  perPage: number;
  handlePerPageChange: (newPerPage: any) => void;
};

type ColunsBudgetProps = {
  id: string;
  total: string;
  status: string;
  user: string;
  patient: string;
  created_at: string;
};

export function BudgetDataTable({
  data: { items, data },
  perPage,
  handlePerPageChange,
}: VoucherDataTableProps) {
  const { t } = useTranslation();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  function getData() {
    const data = items.map((item) => ({
      id: item.id,
      total: Number(item.subtotal || item.total),
      status: item.status,
      user: item.user.name,
      patient: item.patient.name,
      discount: item.voucher.type === "percentage" ? Number(item.voucher.value) + '%' : formatValue(Number(item.voucher.discount)),
      total_with_discount: item.total_with_discount,
      created_at: item.created_at,
      budget: item,
    }));
    return data;
  }

  const columns: ColumnDef<ColunsBudgetProps>[] = [
    {
      accessorKey: "patient",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.patient")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("patient")}</div>
      ),
    },
    {
      accessorKey: "total",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.total")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">
          {formatValue(Number(row.getValue("total")))}
        </div>
      ),
    },
    {
      accessorKey: "discount",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.discount")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">
          {row.getValue("discount")}
        </div>
      ),
    },
    {
      accessorKey: "total_with_discount",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.total_with_discount")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">
          {formatValue(Number(row.getValue("total_with_discount")))}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.status")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const isPending = row.original.status.toLowerCase() === 'pending';
        return (
          <div className="flex items-center gap-2">
            <Badge
              data-status={row.original.status.toLowerCase()}
              className='data-[status="pending"]:bg-amber-100 data-[status="pending"]:text-amber-800 data-[status="pending"]:hover:text-amber-800 data-[status="pending"]:hover:bg-amber-200 data-[status="approved"]:bg-teal-100 data-[status="approved"]:text-teal-800 data-[status="approved"]:hover:text-teal-800 data-[status="approved"]:hover:bg-teal-200 data-[status="canceled"]:bg-red-100 data-[status="canceled"]:text-red-800 data-[status="canceled"]:hover:text-red-800 data-[status="canceled"]:hover:bg-red-200 data-[status="finished"]:bg-blue-100 data-[status="finished"]:text-blue-800 data-[status="finished"]:hover:text-blue-800 data-[status="finished"]:hover:bg-blue-200'
            >
              {t(`LABEL.${row.original.status.toLowerCase()}`)}
            </Badge>
            {isPending && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[10px] text-teal-600 border-teal-200 bg-teal-50 hover:bg-teal-100 flex items-center gap-1 shadow-sm transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  useCopilotStore.getState().openCopilot(`Gere uma mensagem amigável e persuasiva de WhatsApp para resgatar o orçamento pendente do paciente ${row.original.patient} no valor de ${formatValue(Number(row.original.total))}. Sugira um pequeno desconto ou facilidade de pagamento para fecharmos negócio agora.`);
                }}
              >
                <Bot className="w-3 h-3" />
                Resgatar (IA)
              </Button>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "user",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.user")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("user")}</div>
      ),
    },

    {
      accessorKey: "created_at",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.created_at")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">
          {formatDate(row.getValue("created_at") as string, false, true)}
        </div>
      ),
    },
  ];


  const dataMemo = useMemo(() => getData(), [items]);

  const columnsMemo: any = useMemo(() => columns, []);

  const table = useReactTable({
    data: dataMemo,
    columns: columnsMemo,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: perPage,
        pageIndex: 0,
      },
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-x-3 max-w-sm">
          <Label>{t("LABEL.show")}</Label>
          <select
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            value={perPage}
            className="py-1 px-2 text-sm rounded-lg border border-zinc-300 bg-zinc-50 focus:border-green-500 outline-none"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <Label>itens</Label>
        </div>

        <div className="flex items-center justify-end gap-x-2">
          {/* <Button variant="secondary">{t("LABEL.export excel")}</Button> */}
          {/* <Button variant="secondary">{t("LABEL.export pdf")}</Button> */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="ml-auto bg-zinc-50 outline-none"
              >
                {t("LABEL.columns")} <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      // onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      onCheckedChange={(value) => {
                        setColumnVisibility((prev) => ({
                          ...prev,
                          [column.id]: !!value,
                        }));
                      }}
                    >
                      {t(`LABEL.${column.id.toLowerCase()}`)}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <>
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <>
                          <TableCell key={cell.id}>
                            <FormBudgets budget={row.original.budget}>
                              <div className="cursor-pointer">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </div>
                            </FormBudgets>
                          </TableCell>
                        </>
                      );
                    })}
                  </TableRow>
                </>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("no results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination data={data} />
    </div>
  );
}
